import { createContext, useContext } from "react";
import { type IViewContext, createViewProvider } from "@msidentity/SISU/flows/view-context";
import { MemberNameType } from "@msidentity/SISU/model/user";
import { type ICountryInfo, getDefaultCountry } from "@msidentity/SISU/utilities/country-helper";
import type { ServerData } from "@msidentity/SISU/utilities/server-data";
import UsernameRecoveryReducer, { type UsernameRecoveryActions } from "./username-recovery-reducer";
import {
  type RecoveryAlias,
  type UsernameRecoveryMemberNamePrefill,
} from "./username-recovery-types";

export type UsernameRecoveryProofState = {
  aliases: RecoveryAlias[];
  hasAssociatedAccounts: boolean;
  isSignInAllowedProof: boolean;
};

export type UsernameRecoveryState = {
  usernameType: UsernameRecoveryMemberNamePrefill;
  // Value used with back-end
  usernameVal: string;
  // Value displayed to the user
  usernameDisplay: string;
  hasPrefill: boolean;
  errorState: string;
  phoneCountryState: ICountryInfo;
  shortLivedToken: string;
  proofState: UsernameRecoveryProofState;
};

export const defaultProofState: UsernameRecoveryProofState = {
  aliases: [],
  hasAssociatedAccounts: false,
  isSignInAllowedProof: false,
};

export const defaultUsernameRecoveryState: UsernameRecoveryState = {
  usernameType: MemberNameType.EASI,
  usernameVal: "",
  hasPrefill: false,
  usernameDisplay: "",
  errorState: "",
  proofState: defaultProofState,
  phoneCountryState: {
    code: "1",
    displayValue: "United States ‏(‎+1)",
    iso: "US",
  },
  shortLivedToken: "",
};

export const UsernameRecoveryContext = createContext<
  IViewContext<UsernameRecoveryState, UsernameRecoveryActions>
>({
  viewState: defaultUsernameRecoveryState,
  dispatchStateChange: () => {
    throw new Error("UsernameRecovery Context not initialized");
  },
});

export const UsernameRecoveryProvider: React.FC<{ initialState: UsernameRecoveryState }> =
  createViewProvider<UsernameRecoveryState, UsernameRecoveryActions>(
    UsernameRecoveryContext,
    UsernameRecoveryReducer,
  );

/* ********* ServerData helpers  ********** */

/**
 * Create a UsernameRecovery state object from ServerData
 * @param serverData The IDP-specific server data object that should be used to create the UsernameRecovery state
 * @returns The IDP-agnostic UsernameRecovery state object created from the server data
 */
export function createUsernameRecoveryState(serverData: ServerData): UsernameRecoveryState {
  const UsernameRecoveryState = { ...defaultUsernameRecoveryState };

  if (serverData.sUsernamePrefill) {
    UsernameRecoveryState.usernameVal = serverData.sUsernamePrefill;
    UsernameRecoveryState.usernameDisplay = serverData.sUsernamePrefill;
    UsernameRecoveryState.hasPrefill = true;
  }

  if (serverData.sMembernameType) {
    // Update this if we ever support more than EASI/Phone as prefill types
    UsernameRecoveryState.usernameType =
      serverData.sMembernameType === MemberNameType.Phone
        ? MemberNameType.Phone
        : MemberNameType.EASI;
  }

  if (serverData.sServerError) {
    UsernameRecoveryState.errorState = serverData.sServerError;
  }

  if (serverData.arrPhoneCountryList && serverData.sPrefSMSCountry) {
    UsernameRecoveryState.phoneCountryState = getDefaultCountry(
      serverData.arrPhoneCountryList,
      serverData.sPrefSMSCountry,
    );
  }

  return UsernameRecoveryState;
}

export const useUsernameRecoveryContext = () => useContext(UsernameRecoveryContext);
